import { ArrowRight, Drop, MapPin, Ruler, Wall } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { guides, services } from '../content/site';
import { Callout } from '../components/Callout';
import { CraftStory } from '../components/CraftStory';
import { CustomerReviews } from '../components/CustomerReviews';
import { Reveal } from '../components/Reveal';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ServiceCard } from '../components/ServiceCard';
import { HeritageLedgerHero } from '../designs/heritage-ledger/Hero';
import { getFeaturedServices } from '../lib/serviceDisplay';

const principles = [
  { icon: Ruler, title: 'Use', text: 'How the space will carry people, vehicles, soil, or structures.' },
  { icon: Drop, title: 'Water', text: 'Where rain comes from and where it can move safely.' },
  { icon: Wall, title: 'Support', text: 'A properly prepared base or project-appropriate footing beneath the visible finish.' },
  { icon: MapPin, title: 'Place', text: 'Access, grade, exposure, and local requirements around the property.' },
];

const craftStorySteps = [
  {
    kicker: 'Plan around the property',
    title: 'Read the site before choosing the detail.',
    text: 'Use, access, grade, exposure, and water movement shape the scope before the first course or slab is placed.',
    projectType: 'Concrete and grading',
    photoCaption: 'A long access walk graded to follow the building entrance.',
    image: '/images/owner-projects/concrete-long-sidewalk.webp',
    alt: 'Long finished concrete access walk beside a building',
    width: 603,
    height: 452,
    imagePosition: '50% 50%',
  },
  {
    kicker: 'Prepare the support',
    title: 'Build the base or footing for the work above it.',
    text: 'Firm-looking ground is not treated as finished support. The base or project-appropriate footing is prepared for the material, load, and site.',
    projectType: 'Concrete preparation',
    photoCaption: 'Compacted aggregate and forms prepared before a patio placement.',
    image: '/images/owner-projects/concrete-patio-base-prep.webp',
    alt: 'Compacted aggregate base and forms prepared for a concrete patio',
    width: 1024,
    height: 768,
    imagePosition: '50% 50%',
  },
  {
    kicker: 'Build the complete assembly',
    title: 'Connect masonry, drainage, and transitions.',
    text: 'Visible masonry works with the support, adjacent materials, openings, and water-management details around it as one complete assembly.',
    projectType: 'New-construction masonry',
    photoCaption: 'Chimney, brick, concrete, and exterior transitions in progress.',
    image: '/images/owner-projects/new-construction-house-chimney.webp',
    alt: 'Wide view of chimney and exterior masonry during a new-construction project',
    width: 1024,
    height: 768,
    imagePosition: '50% 50%',
  },
  {
    kicker: 'Finish for daily use',
    title: 'Make the final pattern feel settled into the property.',
    text: 'Edges, joints, color, texture, and clean transitions bring the technical work and the finished space together.',
    projectType: 'Pavers and hardscape',
    photoCaption: 'A completed paver patio with a clean lawn transition.',
    image: '/images/owner-projects/paver-patio-finished.webp',
    alt: 'Finished paver patio behind an Eastern North Carolina home',
    width: 603,
    height: 452,
    imagePosition: '50% 50%',
  },
];

export function Home() {
  const featuredServices = getFeaturedServices(services);

  return (
    <>
      <div className="hero-shell section-shell"><HeritageLedgerHero /></div>

      <Reveal as="section" className="principle-strip section-shell" aria-label="Project planning principles">
        {principles.map(({ icon: Icon, title, text }) => (
          <div key={title}>
            <Icon aria-hidden="true" size={23} weight="duotone" />
            <h2>{title}</h2>
            <p>{text}</p>
          </div>
        ))}
      </Reveal>

      <section className="home-services section-pad section-shell">
        <Reveal className="section-heading">
          <p className="section-kicker">Crafted for your property</p>
          <h2>Thoughtful hardscapes for luxury outdoor living.</h2>
          <p>Bring a more refined finish to patios, garden paths, and the approach to your home. Paver pattern, color, edges, and drainage are planned together so the finished space feels at home on your property.</p>
        </Reveal>
        <div className="service-bento">
          {featuredServices.map((service, index) => (
            <Reveal
              key={service.slug}
              delay={Math.min(index * 0.055, 0.28)}
              variant="course"
              style={{ '--course-shift': `${index % 2 === 0 ? -14 : 14}px` }}
            >
              <ServiceCard
                service={service}
                image
                imageSizes="(max-width: 767px) calc(100vw - 24px), (max-width: 1023px) calc(50vw - 24px), min(31vw, 425px)"
              />
            </Reveal>
          ))}
        </div>
        <div className="home-services__links">
          <Link className="text-link" to="/paver-driveways-hardscapes">Explore premium paver projects <ArrowRight aria-hidden="true" size={17} /></Link>
          <Link className="text-link" to="/services">View all services <ArrowRight aria-hidden="true" size={17} /></Link>
        </div>
      </section>

      <CraftStory steps={craftStorySteps} />

      <CustomerReviews />

      <section className="area-feature section-pad section-shell">
        <Reveal className="area-feature__copy">
          <p className="section-kicker">Based in Havelock</p>
          <h2>Built around Eastern North Carolina conditions.</h2>
          <p>Low-relief sites, coastal exposure, summer heat, and heavy rain make material compatibility and water management worth planning early.</p>
          <Link className="button button--secondary" to="/service-area">Explore the Service Area</Link>
        </Reveal>
        <Reveal className="area-feature__places" delay={0.08}>
          {['Havelock', 'New Bern', 'Morehead City', 'Beaufort', 'Jacksonville', 'Greenville'].map((place) => <span key={place}>{place}</span>)}
          <small>Project availability varies by location and scope.</small>
        </Reveal>
      </section>

      <section className="guide-preview section-pad section-shell">
        <Reveal className="section-heading">
          <h2>Know what to ask before work begins.</h2>
          <p>Plain-language project guides for masonry, concrete, retaining walls, and drainage.</p>
        </Reveal>
        <div className="guide-preview__grid">
          {guides.map((guide, index) => (
            <Reveal as="article" key={guide.slug} delay={index * 0.06} variant="media">
              <ResponsiveImage src={guide.image} alt="" width="700" height="460" loading="lazy" />
              <div><span>{guide.readTime}</span><h3><Link to={`/guides/${guide.slug}`}>{guide.title}</Link></h3><p>{guide.description}</p></div>
            </Reveal>
          ))}
        </div>
      </section>

      <Callout />
    </>
  );
}
